import { getVariantUrl } from '@99designs/design-services-common';
import { isComponentMessage } from '../utils/isComponentMessage';
import { parseComponentPayload } from '../utils/parseComponentPayload';
import { resolveCtaUrl } from '../utils/resolveCtaUrl';
import type { ComponentMessage, ImageAttachmentMessage, Message, Role } from '../types/chat';
import type { UploadedAttachment } from '../types/selectors';

const EXTENSION_TO_MIME: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    bmp: 'image/bmp',
    pdf: 'application/pdf',
    doc: 'application/msword',
};

function mediaTypeFromFileName(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    return EXTENSION_TO_MIME[ext] ?? 'application/octet-stream';
}

/**
 * Raw chat-session record returned by the `BriefChatSession` GraphQL query.
 * Kept role-agnostic so callers can pass the API shape directly without
 * having to know about the internal `Role` union.
 */
export interface ChatSessionRecord {
    role: string;
    content: string;
}

interface DecodedContent {
    text: string;
    component: string | Record<string, unknown> | null;
    isBriefComplete: boolean;
}

function decodeContent(content: string): DecodedContent {
    try {
        const parsed = JSON.parse(content);
        if (parsed && typeof parsed === 'object') {
            const text = typeof parsed.message === 'string' ? parsed.message : content;
            const component: string | Record<string, unknown> | null =
                typeof parsed.component === 'string'
                    ? parsed.component
                    : typeof parsed.component === 'object' && parsed.component !== null
                    ? parsed.component
                    : null;
            const isBriefComplete = parsed.inferredState?.isBriefComplete === true;
            return { text, component, isBriefComplete };
        }
    } catch {
        // not JSON, use as-is
    }
    return { text: content, component: null, isBriefComplete: false };
}

/**
 * Map server-side chat session into the internal `Message` shape used by
 * `useChat`. Roles are normalised: `assistant` (Claude's term) and `agent`
 * both map to `agent`; everything else falls back to `user`.
 */
export interface HydrateChatSessionResult {
    messages: Message[];
    isBriefComplete: boolean;
}

export function hydrateChatSession(
    session: readonly ChatSessionRecord[]
): HydrateChatSessionResult {
    const messages: Message[] = [];
    let isBriefComplete = false;

    for (const record of session) {
        const role: Role =
            record.role === 'assistant' || record.role === 'agent' ? 'agent' : 'user';

        // Check for user-side component messages (new JSON pattern)
        if (role === 'user') {
            const componentData = isComponentMessage(record.content);
            if (componentData) {
                messages.push({
                    type: 'componentMessage',
                    role: 'user',
                    componentType: componentData.type,
                    data: componentData.data as Record<string, unknown>,
                } satisfies ComponentMessage);
                continue;
            }

            // Re-hydrate image-upload messages as imageAttachment bubbles.
            try {
                const parsed = JSON.parse(record.content);
                if (parsed?.type === 'image-upload' && Array.isArray(parsed?.data?.value)) {
                    const images: UploadedAttachment[] = parsed.data.value.map(
                        (img: {
                            key: string;
                            name: string;
                            bucket: string;
                            previewable: boolean;
                            size?: number;
                        }) => ({
                            uploadId: img.key,
                            fileName: img.name,
                            previewUrl: getVariantUrl(img.key, 'preview'),
                            downloadUrl: getVariantUrl(img.key, 'original'),
                            mediaType: mediaTypeFromFileName(img.name),
                            fileSize: img.size != null ? String(img.size) : undefined,
                        })
                    );
                    messages.push({
                        type: 'imageAttachment',
                        role: 'user',
                        images,
                        caption: parsed.data.message || undefined,
                    } satisfies ImageAttachmentMessage);
                    continue;
                }
            } catch {
                // not an image upload message, fall through
            }
        }

        const {
            text,
            component,
            isBriefComplete: recordBriefComplete,
        } = decodeContent(record.content);

        if (recordBriefComplete) {
            isBriefComplete = true;
        }

        messages.push({
            type: 'text',
            role,
            content: text,
            skipAnimation: true,
        });

        if (component) {
            const parsedComponent = parseComponentPayload(component);
            if (parsedComponent) {
                if (parsedComponent.type === 'cta-link') {
                    const ctaLink = resolveCtaUrl(parsedComponent.urlKey, parsedComponent.label);
                    if (ctaLink) {
                        const lastMessage = messages[messages.length - 1];
                        if (lastMessage?.type === 'text' && lastMessage.role === 'agent') {
                            lastMessage.ctaLink = ctaLink;
                        }
                    }
                } else {
                    messages.push({
                        type: 'componentRequest',
                        role: 'agent',
                        component: parsedComponent,
                    });
                }
            }
        }
    }

    return { messages, isBriefComplete };
}
